/* © CNES */
import { useQuery } from '@tanstack/react-query';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { OverlayPanel } from 'primereact/overlaypanel';
import { SelectButton } from 'primereact/selectbutton';
import { classNames } from 'primereact/utils';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { sendFeedback } from '../../api/feedback/Feedback';
import { ConfigContext } from '../../context/ConfigContext';
import './Feedback.scss';
import { ReactComponent as CloseIcon } from './icons/close.svg';
import { ReactComponent as FeedbackIcon } from './icons/feedback.svg';

/**
 * Feedback component to display user form
 * @returns element
 */
export const Feedback = () => {
  const navigate = useNavigate();
  const context = useContext(ConfigContext);
  const [modal, setModal] = useState(false);
  const op = useRef<OverlayPanel>(null);
  const [feedbackEmail, setFeedbackEmail] = useState<string>('');
  const [feedbackType, setFeedbackType] = useState<number | null>(null);
  const [feedbackMessage, setFeedbackMessage] = useState<string>('');
  const [loading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);
  const [submitted, setSubmitted] = useState<boolean>(false);
  const [feedbackError, setFeedbackError] = useState<boolean>(false);
  const isValidEmail = /^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/g;
  const items = [
    { name: 'General', value: 1 },
    { name: 'Bug', value: 2 },
    { name: 'Idea', value: 3 },
  ];

  /**
   * API service for feedback post
   * user and product-title params are required for service
   */
  const { refetch, isSuccess } = useQuery({
    queryKey: [
      'Feedback',
      {
        inputs: {
          'feedback-email': feedbackEmail,
          type: items.find((el) => el.value === feedbackType)?.name,
          body: feedbackMessage,
          user: '',
          'product-title': '',
        },
        Navigate,
      },
      navigate,
      context.setError400,
    ],
    queryFn: sendFeedback,
    enabled: false,
  });

  /**
   * create ref for isSuccess var to be used inside setTimeOut
   */
  const isSuccessRef = useRef(isSuccess);
  isSuccessRef.current = isSuccess;

  /**
   * open the feedback modal on click
   * @param e click event
   */
  const onFeedbackButtonClick = (e: React.MouseEvent) => {
    if (op.current) {
      op.current.toggle(e);
    }
    setModal(!modal);
  };

  /**
   * function handled when user send feedback
   * @param e click event
   * @returns
   */
  const handleSubmit = (e: React.MouseEvent) => {
    if (
      !feedbackType ||
      !feedbackMessage ||
      (feedbackEmail && !feedbackEmail.match(isValidEmail))
    ) {
      setError(true);
      return;
    }
    setIsLoading(true);
    refetch();
    setTimeout(() => {
      setIsLoading(false);
      if (isSuccessRef) {
        setSubmitted(true);
        setTimeout(() => {
          op?.current?.toggle(e);
          setModal(false);
        }, 1000);
      } else {
        setFeedbackError(true);
      }
    }, 1000);
  };

  /**
   * reset all state on feedback modal close
   */
  const resetFeedbackForm = () => {
    setFeedbackEmail('');
    setFeedbackType(null);
    setFeedbackMessage('');
    setSubmitted(false);
    setFeedbackError(false);
    setError(false);
  };

  const buttonText = useMemo(() => {
    if (submitted) return 'Feedback sent !';
    else if (feedbackError) return 'An error was occurred !';
    else return 'Send Feedback';
  }, [feedbackError, submitted]);

  const handleResize = () => {
    // Logic to adjust the position of the OverlayPanel
    if (op.current) {
      const target = document.querySelector('.frf-trigger-button');
      if (target && modal) {
        // Adding a slight delay to ensure elements are ready
        setTimeout(() => {
          op.current?.show(null, target);
        }, 100);
      }
    }
  };

  useEffect(() => {
    window.addEventListener('resize', handleResize);
    // Cleanup event listener on component unmount
    return () => window.removeEventListener('resize', handleResize);
    // eslint-disable-next-line
  }, [modal]);

  return (
    <div>
      <div
        className="frf-trigger-button"
        onClick={(e) => onFeedbackButtonClick(e)}
        data-testid="feedback-button"
      >
        <div
          className={modal ? 'frf-feedback-icon-open' : 'frf-feedback-icon'}
          style={{
            fill: '#fff',
          }}
        >
          {modal ? <CloseIcon /> : <FeedbackIcon />}
        </div>
      </div>
      <OverlayPanel
        className="feedback-overlay"
        dismissable={false}
        ref={op}
        onHide={resetFeedbackForm}
      >
        <div className="flex flex-column gap-2">
          <label htmlFor="email">Email</label>
          <InputText
            id="email"
            aria-label="email"
            aria-describedby="username-help"
            onChange={(e) => setFeedbackEmail(e.target.value)}
            type="email"
            placeholder="Enter your email"
            className={classNames({
              'p-invalid': feedbackEmail && !feedbackEmail.match(isValidEmail),
            })}
          />
          <label htmlFor="feedback-type">Feedback Type *</label>
          <SelectButton
            id="feedback-type"
            value={feedbackType}
            onChange={(e) => setFeedbackType(e.value)}
            optionLabel="name"
            options={items}
            className={classNames('feedback-select-button', {
              'p-invalid': error && !feedbackType,
            })}
          />
          <label htmlFor="feedback-message">Feedback Message *</label>
          <InputTextarea
            id="feedback-message"
            className={classNames('feedback-message-textarea', {
              'p-invalid': error && !feedbackMessage,
            })}
            placeholder="Enter your feedback for GEODES"
            value={feedbackMessage}
            onChange={(e) => setFeedbackMessage(e.target.value)}
            rows={5}
            cols={30}
            autoResize
          />
          <Button
            className="frf-modal-button"
            aria-label="handle-submit"
            disabled={false}
            type="submit"
            onClick={(e) => handleSubmit(e)}
          >
            {loading ? (
              <div className="frf-modal-button-loader"></div>
            ) : (
              <span>{buttonText}</span>
            )}
          </Button>
        </div>
      </OverlayPanel>
    </div>
  );
};
